import React, { useState, useMemo, useCallback } from "react";
import { Field, Formik, Form } from "formik";
import {
  FormGroup,
  FormControl,
  Row,
  Col,
  FormCheck,
  Spinner,
} from "react-bootstrap";
import * as Yup from "yup";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

import PurchasePopup from "./PurchasePopup";
import { createSubscription } from "../../actions/Plan";
import { IMAGES, PLAN_CURRENCY } from "../../constants";
import RippleButton from "./RippleButton";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("*Your card's name is incomplete."),
  agreed: Yup.bool().oneOf([true], "*Field must be checked"),
});

const useOptions = () => {
  const options = useMemo(
    () => ({
      style: {
        base: {
          fontFamily: "Roboto",
          fontSize: "16px",
          color: "#929292",
          "::placeholder": {
            color: "#6c757d",
          },
        },
        invalid: {
          color: "#929292",
        },
      },
    }),
    []
  );

  return options;
};

const Payment = ({ createSubscription, auth: { accountInfo }, plan = {} }) => {
  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();
  const [content, setContentModal] = useState(null);
  const [isProcessing, setProcessing] = useState(false);

  const { name, email, company } = accountInfo || {};
  const { id, price = {}, interval } = plan || {};
  const pricePlan = `${PLAN_CURRENCY[price.currency]}${price.amount}`;
  const intervalPlan = interval === "year" ? "annual" : interval;

  const handleDismissModal = useCallback(() => setContentModal(null), []);

  const handlePurchase = async () => {
    setProcessing(true);

    try {
      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });

      if (error) {
        setContentModal(error.message);
      } else {
        await createSubscription({
          account: {
            name: name.trim(),
            email,
            companyName: company,
          },
          planId: id,
          paymentMethodId: paymentMethod.id,
        });
        setContentModal("success");
      }
    } catch (err) {
      setContentModal(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          name: "",
          total: `${pricePlan}/${intervalPlan}`,
          agreed: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handlePurchase}
      >
        {({ errors, touched, dirty }) => (
          <Form className="onboarding-form text-center bg-white p-4">
            <p className="onboarding-plan-name text-left">Payment</p>
            <p className="onboarding-form-note text-left">
              Eliminate the hassle, cost, and risk of single-use paper menus.
            </p>
            <Field name="name">
              {({ field }) => (
                <FormGroup controlId="name" className="onboarding-form-group">
                  <FormControl
                    type="text"
                    placeholder="Name on Card"
                    onChange={field.onChange}
                    value={field.value}
                    data-prop="name"
                    isValid={touched.name && !errors.name}
                    isInvalid={touched.name && errors.name}
                    data-testid="onboarding-input-card-name"
                    className="onboarding-input"
                  />
                  <FormControl.Feedback type="invalid" className="text-left">
                    {errors.name}
                  </FormControl.Feedback>
                </FormGroup>
              )}
            </Field>
            <Field name="card">
              {() => (
                <FormGroup controlId="card" className="onboarding-form-group">
                  <FormControl
                    type="text"
                    placeholder="Credit card number"
                    data-prop="card"
                    data-testid="onboarding-input-card-number"
                    options={options}
                    as={CardNumberElement}
                    className="onboarding-input payment-input"
                  />
                </FormGroup>
              )}
            </Field>
            <Row>
              <Col>
                <Field name="year">
                  {() => (
                    <FormGroup
                      controlId="year"
                      className="onboarding-form-group"
                    >
                      <FormControl
                        type="text"
                        placeholder="Year"
                        data-prop="year"
                        data-testid="onboarding-input-year"
                        options={options}
                        as={CardExpiryElement}
                        className="onboarding-input payment-input"
                      />
                    </FormGroup>
                  )}
                </Field>
              </Col>
              <Col>
                <Field name="cvv">
                  {() => (
                    <FormGroup
                      controlId="cvv"
                      className="onboarding-form-group"
                    >
                      <FormControl
                        type="text"
                        placeholder="CVV"
                        data-prop="cvv"
                        data-testid="onboarding-input-cvv"
                        options={options}
                        as={CardCvcElement}
                        className="onboarding-input payment-input"
                      />
                    </FormGroup>
                  )}
                </Field>
              </Col>
            </Row>
            <Row className="align-items-baseline">
              <Col xs={4} md={5} lg={6}>
                <img src={IMAGES.STRIPE_LOGO} alt="stripe-logo" width="100%" />
              </Col>
              <Col xs={8} md={7} lg={6}>
                <Field name="total">
                  {({ field }) => (
                    <FormGroup controlId="total">
                      <p className="onboarding-total-due text-left">
                        <strong>Total Due:</strong>{" "}
                        <span className="pl-2">{field.value}</span>
                      </p>
                    </FormGroup>
                  )}
                </Field>
              </Col>
            </Row>
            <Field name="agreed">
              {({ field }) => (
                <FormGroup controlId="agreed">
                  <FormCheck
                    type="checkbox"
                    onChange={field.onChange}
                    value={field.value}
                  >
                    <FormCheck.Input
                      type="checkbox"
                      onChange={field.onChange}
                      value={field.value}
                      data-testid="onboarding-input-condition"
                      className="onboarding-input-condition"
                    />
                    <FormCheck.Label className="onboarding-condition">
                      I have read and agreed to SproutQR{" "}
                      <a
                        href=" https://bc-bw-terms-and-conditions.s3.amazonaws.com/BlueCart_Terms_and_Conditions_2024.pdf"
                        target="blank"
                      >
                        Term of Service
                      </a>
                    </FormCheck.Label>
                  </FormCheck>
                </FormGroup>
              )}
            </Field>

            <RippleButton
              type
              testid="submit-payment"
              disabled={!dirty || errors.agreed || isProcessing || !stripe}
            >
              Purchase
              {isProcessing && (
                <Spinner
                  animation="border"
                  className="ml-1"
                  as="span"
                  size="sm"
                  role="isProcessing"
                  aria-hidden="true"
                />
              )}
            </RippleButton>
          </Form>
        )}
      </Formik>
      <PurchasePopup content={content} onDismiss={handleDismissModal} />
    </>
  );
};

Payment.propTypes = {
  createSubscription: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  plan: PropTypes.object.isRequired,
  restaurant: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  plan: state.plan.selectedPlan,
});

export default connect(mapStateToProps, {
  createSubscription,
})(Payment);
